import React, { useCallback, useState } from 'react';
import { Modal } from 'antd';
import ViewNotes from '../../../ManageNodes/ViewNotes';
import type { OrderDetails } from '../../types';

interface ViewNotesModalProps {
  open: boolean;
  /** The order whose notes/documents to show (provides pacs_order.patient) */
  details: OrderDetails;
  onClose: () => void;
}

/**
 * Self-contained wrapper around the shared <ViewNotes> dialog used on the PACS
 * page. It owns the small bit of note-selection / edit state that ViewNotes
 * needs so callers only have to pass `open`, the `details` order, and `onClose`.
 */
const ViewNotesModal: React.FC<ViewNotesModalProps> = ({ open, details, onClose }) => {
  const [selectedNote, setSelectedNote] = useState<any>({});
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState<null | (() => Promise<boolean> | boolean)>(null);

  const handleNoteSelection = useCallback((note: any) => {
    setSelectedNote(prev => {
      const prevId = prev?.rn_id || null;
      const nextId = note?.rn_id || null;
      const prevText = prev?.rn_notes || '';
      const nextText = note?.rn_notes || '';
      if (prevId === nextId && prevText === nextText) return prev;
      return note || {};
    });
  }, []);

  const handleCancel = () => {
    // While editing a note, Cancel just exits edit mode (mirrors PACS behaviour).
    if (noteEditActive) {
      setNoteEditActive(false);
      return;
    }
    onClose();
  };

  const handleOk = async () => {
    if (saveNoteHandler) {
      const saved = await saveNoteHandler();
      if (!saved) return;
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      style={{ top: '5%' }}
      width="90%"
      okButtonProps={{ style: { marginTop: '20px' }, disabled: !noteEditActive }}
      okText="Save"
      cancelText={noteEditActive ? 'Cancel' : 'Close'}
      destroyOnClose
    >
      <ViewNotes
        handleNoteSelection={handleNoteSelection}
        selectedNote={selectedNote}
        viewNotesModal={{ visible: open, details }}
        isEditMode={noteEditActive}
        onEditNoteStateChange={setNoteEditActive}
        onSaveNoteRegister={setSaveNoteHandler}
      />
    </Modal>
  );
};

export default ViewNotesModal;
